import {put, call, all} from 'redux-saga/effects';
import FlatFeeMilestone from "../../entities/flatFeeMilestone";
import * as Actions from "../actions/flatFeeMilestoneActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FlatFeeMilestoneAction = {
    flatFeeMilestoneId: string;
    flatFeeMilestone: FlatFeeMilestone;
    flatFeeMilestones: FlatFeeMilestone[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneFlatFeeMilestone(action: FlatFeeMilestoneAction) {
	const session = tabSession.captureGeneration();
	try {
		const flatFeeMilestone: FlatFeeMilestone = yield call(api.findOne, 'flatFeeMilestone', action.flatFeeMilestoneId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_FETCH_ONE_SUCCESS, flatFeeMilestoneId: action.flatFeeMilestoneId, flatFeeMilestone, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_FETCH_ONE_FAILURE, flatFeeMilestoneId: action.flatFeeMilestoneId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FlatFeeMilestoneAction} action
 */
export function* saveFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FLATFEEMILESTONE_SAVE saga', action.flatFeeMilestone?.id);

		if (action.flatFeeMilestone === undefined || action.flatFeeMilestone === null) {
			console.error('saveFlatFeeMilestone received a null');
			throw new Error('saveFlatFeeMilestone received a null');
		}

		if (!(action.flatFeeMilestone instanceof FlatFeeMilestone)) {
			action.flatFeeMilestone = new FlatFeeMilestone(action.flatFeeMilestone);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.flatFeeMilestone.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.flatFeeMilestone);
			const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
			if (addResult.isErr()) {
				console.error(`⮂ saveFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertFlatFeeMilestoneSave(action.flatFeeMilestone, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.FLATFEEMILESTONE_SAVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.FLATFEEMILESTONE_SAVE, flatFeeMilestone: action.flatFeeMilestone, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertFlatFeeMilestoneSave(action.flatFeeMilestone, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.FLATFEEMILESTONE_SAVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {FlatFeeMilestoneAction} action
 * @param {FlatFeeMilestone[]} action.flatFeeMilestones
 */
export function* saveManyFlatFeeMilestones(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FLATFEEMILESTONE_SAVE_MANY saga', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));

        if (!Array.isArray(action.flatFeeMilestones)) {
            console.error('saveManyFlatFeeMilestones expected an array, received', action.flatFeeMilestones);
            throw new Error('saveManyFlatFeeMilestones expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveFlatFeeMilestone), under these queue keys
		const dispatchedAddedOns: number[] = action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone?.addedOn);
		const revertAll = () => action.flatFeeMilestones.map((flatFeeMilestone, i) =>
			put(Actions.revertFlatFeeMilestoneSave(flatFeeMilestone, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: FlatFeeMilestone[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.flatFeeMilestones.length; i++) {
				const flatFeeMilestone = action.flatFeeMilestones[i];
				flatFeeMilestone.persisted = false;
				ensureOperationId(flatFeeMilestone);
				toSave.push(flatFeeMilestone);
			}

			const addResult = yield call(idb.add, 'flatFeeMilestone', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyFlatFeeMilestones: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY_FAILURE, flatFeeMilestones: action.flatFeeMilestones, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY, flatFeeMilestones: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY_FAILURE, flatFeeMilestones: action.flatFeeMilestones, status: 500, err});

			console.error(`Error saving ${action.flatFeeMilestones.length} flatFeeMilestones to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {FlatFeeMilestoneAction} action
 */
export function* removeFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.flatFeeMilestone === undefined || action.flatFeeMilestone === null || !(action.flatFeeMilestone instanceof FlatFeeMilestone)) {
			console.error('removeFlatFeeMilestone expected type FlatFeeMilestone, received', action.flatFeeMilestone);
			reportQueued(false);
			throw new Error('removeFlatFeeMilestone expected type FlatFeeMilestone');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.flatFeeMilestone.status = 'D';
		action.flatFeeMilestone.addedOn = newHRTimestamp();
		action.flatFeeMilestone.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.flatFeeMilestone);
		const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.FLATFEEMILESTONE_REMOVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_REMOVE, flatFeeMilestone: action.flatFeeMilestone});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_REMOVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this FlatFeeMilestone.
	*
	* @param {FlatFeeMilestoneAction} action
	*/
export function* restoreFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFlatFeeMilestone expected getCurrentUserId() to return a value');
        return;
    }

	if (action.flatFeeMilestone === undefined || action.flatFeeMilestone === null) {
        console.error('restoreFlatFeeMilestone expected a FlatFeeMilestone, received null');
        throw new Error('restoreFlatFeeMilestone expected a FlatFeeMilestone, received null');
    }
    else if (!(action.flatFeeMilestone instanceof FlatFeeMilestone)) action.flatFeeMilestone = new FlatFeeMilestone(action.flatFeeMilestone); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.flatFeeMilestone.status = 'U';
		action.flatFeeMilestone.addedOn = newHRTimestamp();
		action.flatFeeMilestone.addedBy = userId;
		action.flatFeeMilestone.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.flatFeeMilestone);
		const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEEMILESTONE_RESTORE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_RESTORE, flatFeeMilestone: action.flatFeeMilestone});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_RESTORE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

		console.error('Error restoring from database', err);
	}
}